import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CertificationQuestionsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const PAGE_SIZE = 20;

function CertificationQuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [formData, setFormData] = useState({
    topic_id: '',
    text: '',
    answer_options: ['', ''],
    correct_answers: []
  });
  const [topicFilter, setTopicFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  /** Результат проверки по ID вопроса: { [questionId]: true | false }. Только в памяти, не сохраняется. */
  const [checkResultByQuestionId, setCheckResultByQuestionId] = useState({});
  /** ID вопроса, для которого идёт запрос проверки (показываем загрузку). */
  const [checkingQuestionId, setCheckingQuestionId] = useState(null);
  /** ID вопроса, для которого идёт пересоздание вариантов ответов (показываем загрузку на кнопке). */
  const [regeneratingAnswerQuestionId, setRegeneratingAnswerQuestionId] = useState(null);

  useEffect(() => {
    const loadTopics = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/certification-topics`);
        setTopics(response.data);
      } catch (err) {
        console.error('Ошибка при загрузке тем:', err);
      }
    };
    loadTopics();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, topicFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * PAGE_SIZE;
      const params = { skip, limit: PAGE_SIZE };
      if (topicFilter) {
        params.topic_id = parseInt(topicFilter);
      }
      const countParams = topicFilter ? { topic_id: parseInt(topicFilter) } : {};
      const [questionsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/certification-questions`, { params }),
        axios.get(`${API_URL}/api/certification-questions/count`, { params: countParams })
      ]);
      setQuestions(questionsRes.data);
      setTotalCount(countRes.data.count);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Фильтруем пустые варианты ответов
      const validOptions = formData.answer_options.filter(opt => opt.trim() !== '');
      
      if (validOptions.length < 2) {
        setError('Необходимо добавить минимум 2 варианта ответа');
        return;
      }

      if (formData.correct_answers.length === 0) {
        setError('Необходимо выбрать хотя бы один правильный ответ');
        return;
      }

      const payload = {
        topic_id: parseInt(formData.topic_id),
        text: formData.text,
        answer_options: validOptions,
        correct_answers: formData.correct_answers
      };
      
      if (editingQuestionId) {
        await axios.put(`${API_URL}/api/certification-questions/${editingQuestionId}`, payload);
        setEditingQuestionId(null);
      } else {
        await axios.post(`${API_URL}/api/certification-questions`, payload);
        setShowForm(false);
      }
      setFormData({
        topic_id: '',
        text: '',
        answer_options: ['', ''],
        correct_answers: []
      });
      fetchData();
    } catch (err) {
      setError('Ошибка при сохранении вопроса: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (question) => {
    setEditingQuestionId(question.id);
    setFormData({
      topic_id: question.topic_id.toString(),
      text: question.text,
      answer_options: Array.isArray(question.answer_options)
        ? question.answer_options
        : JSON.parse(question.answer_options || '[]'),
      correct_answers: Array.isArray(question.correct_answers)
        ? question.correct_answers
        : JSON.parse(question.correct_answers || '[]')
    });
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот вопрос?')) {
      try {
        await axios.delete(`${API_URL}/api/certification-questions/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении вопроса: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  /** Пересоздать варианты ответов для вопроса через бэкенд и открыть форму редактирования с новыми данными. */
  const handleRegenerateAnswers = async (question) => {
    const questionText = (question.text || '').trim();
    if (!questionText) {
      setError('У вопроса отсутствует текст для пересоздания вариантов ответов');
      return;
    }
    setRegeneratingAnswerQuestionId(question.id);
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/certification-questions/generate-answer`, {
        question_text: questionText,
      });
      let opts = Array.isArray(data.answer_options) ? data.answer_options : [];
      if (opts.length < 4) opts = [...opts, ...Array(4 - opts.length).fill('')];
      if (opts.length > 4) opts = opts.slice(0, 4);
      const correctIndex = Math.max(0, Math.min(3, data.correct_answer_index ?? 0));
      setFormData({
        topic_id: String(question.topic_id),
        text: question.text,
        answer_options: opts,
        correct_answers: [correctIndex],
      });
      setEditingQuestionId(question.id);
    } catch (err) {
      setError('Ошибка при пересоздании вариантов ответов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingAnswerQuestionId(null);
    }
  };

  const handleCheckAnswer = async (question) => {
    const options = formatAnswerOptions(question.answer_options);
    const correctIndices = formatCorrectAnswers(question.correct_answers);
    const correctAnswerTexts = correctIndices
      .map((idx) => options[idx])
      .filter((t) => t != null && String(t).trim() !== '');
    if (correctAnswerTexts.length === 0) {
      setError('Нет вариантов правильного ответа для проверки');
      return;
    }
    setCheckingQuestionId(question.id);
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/certification-questions/validate-answer`, {
        question_text: question.text,
        correct_answers: correctAnswerTexts,
      });
      setCheckResultByQuestionId((prev) => ({ ...prev, [question.id]: data.correct === true }));
    } catch (err) {
      setError('Ошибка при проверке ответа: ' + (err.response?.data?.detail || err.message));
      setCheckResultByQuestionId((prev) => ({ ...prev, [question.id]: false }));
    } finally {
      setCheckingQuestionId(null);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingQuestionId(null);
    setFormData({
      topic_id: '',
      text: '',
      answer_options: ['', ''],
      correct_answers: []
    });
  };

  const addAnswerOption = () => {
    setFormData({
      ...formData,
      answer_options: [...formData.answer_options, '']
    });
  };

  const removeAnswerOption = (index) => {
    const newOptions = formData.answer_options.filter((_, i) => i !== index);
    // Удаляем правильные ответы, которые ссылаются на удаленный вариант
    const newCorrectAnswers = formData.correct_answers
      .filter(ans => ans !== index)
      .map(ans => ans > index ? ans - 1 : ans);
    
    setFormData({
      ...formData,
      answer_options: newOptions,
      correct_answers: newCorrectAnswers
    });
  };

  const updateAnswerOption = (index, value) => {
    const newOptions = [...formData.answer_options];
    newOptions[index] = value;
    setFormData({ ...formData, answer_options: newOptions });
  };

  const toggleCorrectAnswer = (index) => {
    const newCorrectAnswers = formData.correct_answers.includes(index)
      ? formData.correct_answers.filter(ans => ans !== index)
      : [...formData.correct_answers, index].sort((a, b) => a - b);
    
    setFormData({ ...formData, correct_answers: newCorrectAnswers });
  };

  const getTopicTitle = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.title : `ID: ${topicId}`;
  };

  const formatAnswerOptions = (options) => {
    if (Array.isArray(options)) {
      return options;
    }
    try {
      return JSON.parse(options || '[]');
    } catch {
      return [];
    }
  };

  const formatCorrectAnswers = (answers) => {
    if (Array.isArray(answers)) {
      return answers;
    }
    try {
      return JSON.parse(answers || '[]');
    } catch {
      return [];
    }
  };

  const handleFilterChange = (value) => {
    setTopicFilter(value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setTopicFilter('');
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  if (loading && questions.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="certification-questions-tab">
      <div className="tab-header">
        <h3>Управление вопросами сертификаций</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить вопрос
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="filter-topic">Сертификация:</label>
            <select
              id="filter-topic"
              value={topicFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="filter-select"
            >
              <option value="">Все сертификации</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.title}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-clear-filters"
            onClick={clearFilter}
            disabled={!topicFilter}
          >
            Сбросить фильтр
          </button>
        </div>
        <div className="results-info">
          Найдено вопросов: <strong>{totalCount}</strong>
        </div>
      </div>

      {showForm && !editingQuestionId && (
        <div className="form-container">
          <h4>Добавить новый вопрос</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="topic_id">Тема сертификации *</label>
              <select
                id="topic_id"
                value={formData.topic_id}
                onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                required
                disabled={topics.length === 0}
              >
                <option value="">Выберите тему</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.title}
                  </option>
                ))}
              </select>
              {topics.length === 0 && (
                <small className="form-hint">
                  Сначала создайте тему сертификации
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="text">Текст вопроса *</label>
              <textarea
                id="text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                required
                rows={4}
                placeholder="Введите текст вопроса"
              />
            </div>
            <div className="form-group">
              <label>Варианты ответов *</label>
              {formData.answer_options.map((option, index) => (
                <div key={index} className="answer-option-row">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateAnswerOption(index, e.target.value)}
                    placeholder={`Вариант ${index + 1}`}
                    className="answer-option-input"
                  />
                  <button
                    type="button"
                    className={`btn-checkbox ${formData.correct_answers.includes(index) ? 'checked' : ''}`}
                    onClick={() => toggleCorrectAnswer(index)}
                    title={formData.correct_answers.includes(index) ? 'Правильный ответ' : 'Отметить как правильный'}
                  >
                    {formData.correct_answers.includes(index) ? '✓' : '○'}
                  </button>
                  {formData.answer_options.length > 2 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeAnswerOption(index)}
                      title="Удалить вариант"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-add-option"
                onClick={addAnswerOption}
              >
                + Добавить вариант ответа
              </button>
              <small className="form-hint">
                Минимум 2 варианта ответа. Отметьте правильные ответы, нажав на кружок рядом с вариантом.
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={topics.length === 0}>
                Создать
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Тема</th>
              <th>Текст вопроса</th>
              <th>Варианты ответов</th>
              <th>Правильные ответы</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Загрузка...
                </td>
              </tr>
            ) : questions.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  {topicFilter
                    ? 'Нет вопросов для выбранной сертификации.'
                    : 'Нет вопросов. Добавьте первый вопрос сертификации.'}
                </td>
              </tr>
            ) : (
              questions.map((question) => {
                const options = formatAnswerOptions(question.answer_options);
                const correct = formatCorrectAnswers(question.correct_answers);
                const isEditing = editingQuestionId === question.id;
                return (
                  <React.Fragment key={question.id}>
                    {isEditing ? (
                      <tr className="cert-questions-inline-edit-row">
                        <td colSpan="6">
                          <div className="cert-questions-inline-edit-form">
                            <h4>Редактировать вопрос</h4>
                            <form onSubmit={handleSubmit}>
                              <div className="form-group">
                                <label htmlFor={`cert-inline-topic_id-${question.id}`}>Тема сертификации *</label>
                                <select
                                  id={`cert-inline-topic_id-${question.id}`}
                                  value={formData.topic_id}
                                  onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                                  required
                                  disabled={topics.length === 0}
                                >
                                  <option value="">Выберите тему</option>
                                  {topics.map((topic) => (
                                    <option key={topic.id} value={topic.id}>
                                      {topic.title}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label htmlFor={`cert-inline-text-${question.id}`}>Текст вопроса *</label>
                                <textarea
                                  id={`cert-inline-text-${question.id}`}
                                  value={formData.text}
                                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                  required
                                  rows={4}
                                  placeholder="Введите текст вопроса"
                                />
                              </div>
                              <div className="form-group">
                                <label>Варианты ответов *</label>
                                {formData.answer_options.map((option, index) => (
                                  <div key={index} className="answer-option-row">
                                    <input
                                      type="text"
                                      value={option}
                                      onChange={(e) => updateAnswerOption(index, e.target.value)}
                                      placeholder={`Вариант ${index + 1}`}
                                      className="answer-option-input"
                                    />
                                    <button
                                      type="button"
                                      className={`btn-checkbox ${formData.correct_answers.includes(index) ? 'checked' : ''}`}
                                      onClick={() => toggleCorrectAnswer(index)}
                                      title={formData.correct_answers.includes(index) ? 'Правильный ответ' : 'Отметить как правильный'}
                                    >
                                      {formData.correct_answers.includes(index) ? '✓' : '○'}
                                    </button>
                                    {formData.answer_options.length > 2 && (
                                      <button
                                        type="button"
                                        className="btn-remove"
                                        onClick={() => removeAnswerOption(index)}
                                        title="Удалить вариант"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="btn-add-option"
                                  onClick={addAnswerOption}
                                >
                                  + Добавить вариант ответа
                                </button>
                                <small className="form-hint">
                                  Минимум 2 варианта ответа. Отметьте правильные ответы, нажав на кружок рядом с вариантом.
                                </small>
                              </div>
                              <div className="form-actions">
                                <button type="submit" className="btn btn-primary" disabled={topics.length === 0}>
                                  Сохранить
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                                  Отмена
                                </button>
                              </div>
                            </form>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td>{question.id}</td>
                        <td>{getTopicTitle(question.topic_id)}</td>
                        <td className="question-text">
                          {question.text}
                          {checkResultByQuestionId[question.id] === true && (
                            <span className="check-result-badge check-result-correct" title="Проверка пройдена">
                              Ответ правильный
                            </span>
                          )}
                        </td>
                        <td className="options-cell">
                          <ol className="options-list">
                            {options.map((opt, idx) => (
                              <li key={idx} className={correct.includes(idx) ? 'correct' : ''}>
                                {opt}
                              </li>
                            ))}
                          </ol>
                        </td>
                        <td className="correct-answers-cell">
                          {correct.map((idx, i) => (
                            <span key={i} className="correct-badge">
                              {idx + 1}
                            </span>
                          ))}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-regenerate-answers"
                            onClick={() => handleRegenerateAnswers(question)}
                            disabled={regeneratingAnswerQuestionId === question.id || !(question.text || '').trim()}
                            title={
                              !(question.text || '').trim()
                                ? 'Нет текста вопроса для пересоздания'
                                : 'Пересоздать варианты ответов нейросетью и открыть форму для сохранения'
                            }
                          >
                            {regeneratingAnswerQuestionId === question.id ? 'Пересоздание...' : 'Пересоздать варианты'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-check-answer"
                            onClick={() => handleCheckAnswer(question)}
                            disabled={checkingQuestionId === question.id}
                            title="Проверить корректность ответа агентом"
                          >
                            {checkingQuestionId === question.id ? 'Проверка...' : 'Проверка'}
                          </button>
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(question)}
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(question.id)}
                            title="Удалить"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Страница {currentPage} из {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
            >
              ← Назад
            </button>
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    className={`btn pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificationQuestionsTab;

